"use client";
import React, { useCallback, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { IChat, IConfig, IProject, IUser } from "@/lib/types";
import { ChatsContext, UserContext, ConfigContext, ProjectsContext } from "./context";
import {
    getChats,
    getProjects,
    loadConfig,
    saveConfig,
    updateChat as daoUpdateChat,
    deleteChat as daoDeleteChat,
    createProject as daoCreateProject,
    updateProject as daoUpdateProject,
    deleteProject as daoDeleteProject,
} from "@/lib/dao";
import { getJWT } from "@/lib/auth";

export const Providers = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = React.useState<IUser | undefined>(undefined);
    const [chats, setChats] = React.useState<IChat[]>([]);
    const [projects, setProjects] = React.useState<IProject[]>([]);
    const [config, setConfig] = React.useState<IConfig>(loadConfig());

    const { instance, accounts } = useMsal();

    const updateUser = useCallback((inputUser: IUser | undefined) => {
        setUser(inputUser);
    }, []);

    const userContextValue = useMemo(
        () => ({
            user: user,
            updateUser: updateUser,
        }),
        [user, updateUser]
    );

    const updateChat = useCallback(
        async (inputChat: Partial<IChat>, doNetwork: boolean, doUpdateLastmessage = false) => {
            const updateChat = async (inputChat: Partial<IChat>, doNetwork: boolean, doUpdateLastmessage: boolean) => {
                if (doUpdateLastmessage) {
                    inputChat.lastmessage = new Date();
                }
                const existingChat = chats.find((chat) => chat.chatid === inputChat.chatid);
                if (existingChat) {
                    console.log(`${Date.now()} existingChat in updateChat: ${JSON.stringify(existingChat)}`);
                    const updatedChat = { ...existingChat, ...inputChat };
                    setChats(chats.map((chat) => (chat.chatid === inputChat.chatid ? updatedChat : chat)));
                } else {
                    setChats(
                        [...chats, inputChat as IChat].sort((a, b) => b.lastmessage.getTime() - a.lastmessage.getTime())
                    );
                }
                if (doNetwork && inputChat.chatid) {
                    getJWT(instance, accounts).then((jwt) =>
                        daoUpdateChat(jwt, inputChat as Partial<IChat> & Pick<IChat, "chatid">)
                    );
                }
            };
            return updateChat(inputChat, doNetwork, doUpdateLastmessage);
        },
        [accounts, chats, instance]
    );

    const deleteChat = useCallback(
        async (chatid: string) => {
            const deleteChat = async (chatid: string) => {
                setChats(chats.filter((chat) => chat.chatid !== chatid));
                getJWT(instance, accounts).then((jwt) => daoDeleteChat(jwt, chatid));
            };
            return deleteChat(chatid);
        },
        [accounts, chats, instance]
    );

    const chatsContextValue = useMemo(
        () => ({
            chats: chats,
            setChats: setChats,
            updateChat: updateChat,
            deleteChat: deleteChat,
        }),
        [chats, updateChat, deleteChat]
    );

    const createProject = useCallback(
        async (inputProject: Pick<IProject, "title" | "description">): Promise<IProject> => {
            const jwt = await getJWT(instance, accounts);
            const createdProject = await daoCreateProject(jwt, inputProject);
            setProjects([...projects, createdProject]);
            return createdProject;
        },
        [accounts, instance, projects]
    );

    const updateProject = useCallback(
        async (inputProject: Partial<IProject>) => {
            const updateProject = async (inputProject: Partial<IProject>) => {
                const existingProject = projects.find((project) => project.projectid === inputProject.projectid);
                if (existingProject) {
                    const updatedProject = { ...existingProject, ...inputProject };
                    setProjects(
                        projects.map((project) =>
                            project.projectid === inputProject.projectid ? updatedProject : project
                        )
                    );
                } else {
                    setProjects(
                        [...projects, inputProject as IProject].sort((a, b) =>
                            (a.title || "").localeCompare(b.title || "")
                        )
                    );
                }
                getJWT(instance, accounts).then((jwt) =>
                    daoUpdateProject(jwt, inputProject as Partial<IProject> & Pick<IProject, "projectid">)
                );
            };
            return updateProject(inputProject);
        },
        [accounts, instance, projects]
    );

    const deleteProject = useCallback(
        (projectid: string) => {
            const deleteProject = async (projectid: string) => {
                setProjects(projects.filter((project) => project.projectid !== projectid));
                // Remove chats from this project from the navbar list
                if (chats.find((chat) => chat.projectid === projectid)) {
                    setChats(chats.filter((chat) => chat.projectid !== projectid));
                }
                getJWT(instance, accounts).then((jwt) => daoDeleteProject(jwt, projectid));
            };
            return deleteProject(projectid);
        },
        [accounts, chats, instance, projects]
    );

    const projectsContextValue = useMemo(
        () => ({
            projects: projects,
            setProjects: setProjects,
            createProject: createProject,
            updateProject: updateProject,
            deleteProject: deleteProject,
        }),
        [projects, createProject, updateProject, deleteProject]
    );

    const updateConfig = (config: IConfig) => {
        setConfig(config);
        saveConfig(config);
    };

    const configContextValue = {
        config: config,
        updateConfig: updateConfig,
    };

    useEffect(() => {
        // Set the user from MSAL
        if (accounts.length === 0) {
            setUser(undefined);
        } else {
            setUser({
                userid: accounts[0].homeAccountId,
                name: accounts[0].name || "User",
                email: accounts[0].username,
                department: accounts[0].idTokenClaims?.department as string | undefined,
            });
        }
        // Load the config from localstorage
        // We don't have to do this, it's done at the initial provider level
        setConfig(loadConfig());
        // Load the chats and projects from the network
        if (accounts.length > 0) {
            getJWT(instance, accounts).then((jwt) => {
                getChats(jwt).then((chats) => setChats(chats));
                getProjects(jwt).then((projects) => setProjects(projects));
            });
        }
    }, [accounts, instance]);

    return (
        <>
            <UserContext value={userContextValue}>
                <ProjectsContext value={projectsContextValue}>
                    <ChatsContext value={chatsContextValue}>
                        <ConfigContext value={configContextValue}>{children}</ConfigContext>
                    </ChatsContext>
                </ProjectsContext>
            </UserContext>
        </>
    );
};
